import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { notify } from "@/utils";

import {
  conversations,
  fetchConversationByIdQueryOptions,
  fetchGroupInviteQueryOptions,
  useFetchGroupInvite,
  useJoinGroup,
} from "../services";
import type { ConversationList } from "../services/conversation";
import type { GroupInvitePreview } from "../services/groupInvite";
import type { Conversation } from "../types";

function buildSyntheticConversation(
  preview: GroupInvitePreview,
): Conversation<true, true, true> | undefined {
  const conversationId = preview.conversation?.id;
  if (!preview.can_join || !conversationId) return undefined;

  return {
    id: conversationId,
    type: "group",
    message_group_id: preview.id,
    last_messages: [],
    unread_messages_count: 0,
    last_outgoing_read_message_id: null,
    last_message_id_read_by_me: null,
    group: {
      id: preview.id,
      name: preview.name,
      image_url: preview.image_url,
      image_thumb: preview.image_thumb,
    },
  } as Conversation<true, true, true>;
}

function enrichConversationFromPreview(
  preview: GroupInvitePreview,
): Conversation<true, true, true> {
  return {
    ...preview.conversation!,
    group: {
      id: preview.id,
      name: preview.name,
      image_url: preview.image_url,
      image_thumb: preview.image_thumb,
    },
  } as Conversation<true, true, true>;
}

export function useGroupJoinPanel(
  token: string,
  onOpenConversation: (conversation: Conversation<true, true, true>) => void,
) {
  const { t } = useTranslation("messenger.user");
  const queryClient = useQueryClient();
  const { data: preview, isLoading, isError } = useFetchGroupInvite(token);
  const joinGroup = useJoinGroup();

  const syntheticConversation = preview
    ? buildSyntheticConversation(preview)
    : undefined;

  const openConversationById = async (conversationId: number) => {
    const full = await queryClient.fetchQuery(
      fetchConversationByIdQueryOptions(conversationId),
    );
    onOpenConversation(full);
  };

  const openFromCache = (groupId: number): boolean => {
    const cached = queryClient
      .getQueriesData<InfiniteData<ConversationList>>({
        queryKey: conversations.allInfinite._def,
      })
      .flatMap(([, data]) => data?.pages.flatMap((page) => page.data) ?? [])
      .find(
        (item) => item.type === "group" && item.message_group_id === groupId,
      );

    if (!cached) return false;

    queryClient.setQueryData(
      fetchConversationByIdQueryOptions(cached.id).queryKey,
      cached,
    );
    onOpenConversation(cached);
    return true;
  };

  useEffect(() => {
    if (!preview || preview.can_join) return;

    if (preview.conversation) {
      const enriched = enrichConversationFromPreview(preview);
      queryClient.setQueryData(
        fetchConversationByIdQueryOptions(enriched.id).queryKey,
        enriched,
      );
      onOpenConversation(enriched);
      return;
    }

    if (!openFromCache(preview.id)) {
      joinGroup.mutate(token, {
        onSuccess: async (result) => {
          if (result.conversation) {
            await openConversationById(result.conversation.id);
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const handleJoin = () => {
    joinGroup.mutate(token, {
      onSuccess: async (result) => {
        notify.success(t("Joined group successfully"));
        queryClient.invalidateQueries(fetchGroupInviteQueryOptions(token));

        if (result.conversation) {
          await openConversationById(result.conversation.id);
          return;
        }
        openFromCache(result.id);
      },
      onError: () => notify.error(t("Failed to join group")),
    });
  };

  return {
    preview,
    isLoading,
    isError,
    joinGroup,
    syntheticConversation,
    handleJoin,
  };
}
